<template>
  <tr class="bcl-token-list-table-row rounded">
    <td class="cell cell-rank pl-2 pl-md-4">
      <div
        class="rank"
        v-text="useCollectionRank ? token.collection_rank : token.rank"
      />
    </td>
    <td class="cell cell-name px-1 px-md-3">
      <div class="token-name">{{ token.symbol }}</div>
    </td>
    <td class="cell cell-collection text-right px-1 px-md-3">
      <TokenLabel>{{
        $t(`collections.${parseCollectionName(token.collection)}.name`)
      }}</TokenLabel>
    </td>
    <td class="cell cell-price px-1 px-md-3">
      <div class="d-flex d-md-block flex-space-between">
        <div class="mobile-label d-block d-md-none">
          {{ $t("common.price") }}:
        </div>
        <PriceDataFormatter
          :price-data="tokenData.price_data"
          class="justify-end"
          :row="smAndDown"
        />
      </div>
    </td>
    <td class="cell cell-market-cap px-1 px-md-3 text-md-right">
      <div class="d-flex d-md-block flex-space-between">
        <div class="mobile-label d-block d-md-none">
          {{ $t("common.marketCapShort") }}:
        </div>
        <PriceDataFormatter
          bignumber
          :price-data="tokenData.market_cap_data"
          class="justify-end"
          :row="smAndDown"
        />
      </div>
    </td>
    <td class="cell cell-address text-right px-1 px-md-3">
      <AddressChip :address="token.sale_address" />
    </td>
    <td class="cell cell-chart text-right pr-md-4">
      <TokenLineChart
        :sale-address="token.address"
        :height="60"
        chart-type="line"
        class="ml-auto chart"
      />
    </td>
    <td class="cell cell-link">
      <RouterLink
        :to="{
          name: ROUTE_SALE,
          params: { contractAddress: token.sale_address },
        }"
        class="link"
      />
    </td>
  </tr>
</template>

<script lang="ts" setup>
import { defineProps } from "vue";
import { useDisplay } from "vuetify";

import { useLiveTokenData } from "@/composables";

import { ROUTE_SALE } from "@/utils/constants";
import { parseCollectionName } from "@/utils/common";

import PriceDataFormatter from "@/components/Common/Pricing/PriceDataFormatter.vue";
import TokenLineChart from "@/components/Charts/TokenLineChart.vue";
import AddressChip from "../Common/AddressChip.vue";
import TokenLabel from "../Common/TokenLabel.vue";

const props = defineProps({
  token: { type: Object, required: true },
  useCollectionRank: Boolean,
});

const { tokenData } = useLiveTokenData({
  token: props.token,
});

const { smAndDown } = useDisplay();
</script>

<style lang="scss">
@use "@/styles/variables" as *;

.bcl-token-list-table-row {
  position: relative;
  z-index: 1;
  background-color: rgb(var(--v-theme-surface));
  box-shadow: var(--outline);
  transition: all 0.15s;
  transform: translate(0, 0);

  &::before,
  &::after {
    content: "";
    display: block;
    position: absolute;
    z-index: -1;
    border-radius: inherit;
    transition: all 0.15s;
  }

  &::before {
    inset: 0;
    opacity: 0;
    background: linear-gradient(
      0deg,
      rgba(white, 0.2) 20%,
      rgba(white, 0) 100%
    );
  }

  &::after {
    inset: 1px;
    background-color: rgb(var(--v-theme-surface));
  }

  &:hover {
    &::before {
      opacity: 1;
    }

    &::after {
      background-color: #2a2a2a;
    }

    .token-name {
      color: rgb(var(--v-theme-primary));
    }
  }

  &:active {
    transform: translateY(3px);

    &::after {
      background-color: #333333;
    }
  }

  .token-name {
    transition: all 0.15s;
  }

  .cell {
    &-rank {
      width: 52px;
      font-family: $heading-font-family;
      font-weight: bold;
      opacity: 0.5;
    }
  }

  .chart {
    max-width: 140px;
  }

  .link {
    position: absolute;
    z-index: 2;
    inset: 0;
  }

  .mobile-label {
    width: 54px;
  }

  @media screen and (min-width: 961px) {
    .cell {
      &-name {
        font-size: 21px;
      }

      &-price,
      &-market-cap {
        width: 170px;
      }
    }
  }

  @media screen and (max-width: 960px) {
    display: grid;
    grid-template-columns: 32px 5fr 2fr;
    grid-template-rows: 1fr 20px 20px;
    grid-template-areas:
      "rank name       chart"
      "rank price      chart"
      "rank market-cap chart";
    margin-top: 4px;
    padding-block: 8px;

    .cell {
      display: block;

      &-rank {
        grid-area: rank;
        padding-top: 3px;
        font-size: 12px;
        font-family: $body-font-family;
      }

      &-name {
        grid-area: name;
      }

      &-price {
        grid-area: price;
      }

      &-market-cap {
        grid-area: market-cap;
      }

      &-price,
      &-market-cap {
        font-size: 0.814em;
      }

      &-chart {
        grid-area: chart;
      }

      &-collection,
      &-address {
        display: none;
      }
    }
  }
}
</style>
